function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.buttons = this.querySelectorAll('button');
    this.isCartLineQuantity = !!this.closest('cart-items, cart-drawer-items');
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    if (this.isCartLineQuantity && this.classList.contains('quantity--updating')) return;
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    // if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);
    if (previousValue !== this.input.value) {
      if (this.isCartLineQuantity) this.lock();
      this.input.dispatchEvent(this.changeEvent);
    }


    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  lock() {
    this.classList.add('quantity--updating');
    this.buttons.forEach((button) => {
      button.disabled = true;
      button.classList.add('disabled');
    });

    // safety if disableLoading() never runs for any reason
    // (JS error mid-request, section fails to re-render, etc.), don't
    // leave the buttons dead forever.
    clearTimeout(this._unlockFailsafe);
    this._unlockFailsafe = setTimeout(() => this.unlock(), 8000);
  }

  unlock() {
    clearTimeout(this._unlockFailsafe);
    this.classList.remove('quantity--updating');
    this.buttons.forEach((button) => (button.disabled = false));
    this.validateQtyRules();
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section')?.id?.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }
       const shouldBeRemoved = !(this.querySelector('slideshow-component') || this.querySelector('complementary-slider'));

        if (shouldBeRemoved && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}




// -----------------------UNit shipping protection ----------------

// shared mutex so this component and shipping-liner-auto-add.js never mutate the cart concurrently
window.__withCartLock = window.__withCartLock || function (fn) {
  window.__cartOpsQueue = window.__cartOpsQueue || Promise.resolve();
  const run = () => Promise.resolve().then(fn);
  const next = window.__cartOpsQueue.then(run, run);
  window.__cartOpsQueue = next.catch(() => {});
  return next;
};

// Coalesces near-simultaneous cart.js reads across shipping-protection,
// shipping-liner-auto-add, and upsell into a single network request, and
// works around iOS Safari's fetch cache sometimes ignoring cache:'no-store'
// by cache-busting the URL. Falls back to a text->JSON.parse instead of a
// strict content-type check, since Safari doesn't always send a clean
// content-type header on this endpoint.
window.__getCartCached = window.__getCartCached || function () {
  const now = Date.now();
  if (window.__cartCachePromise && (now - (window.__cartCacheTime || 0)) < 400) {
    return window.__cartCachePromise;
  }
  window.__cartCacheTime = now;
  window.__cartCachePromise = fetch(window.Shopify.routes.root + 'cart.js?_=' + now, {
    cache: 'no-store',
    headers: { Accept: 'application/json' }
  })
    .then((response) => {
      if (!response.ok) throw new Error('Cart fetch failed: ' + response.status);
      return response.text();
    })
    .then((text) => {
      try {
        return JSON.parse(text);
      } catch (e) {
        throw new Error('Cart fetch returned unparseable body: ' + text.slice(0, 120));
      }
    });
  return window.__cartCachePromise;
};

const stLog = (...args) => console.log('%c[ShipTection]', ...args);
const stWarn = (...args) => console.warn('[ShipTection]', ...args);

// coalesces near-simultaneous ShipTection /quote calls the same way
// __getCartCached coalesces /cart.js reads. Keyed on the serialized payload
// (not just time) so two calls for genuinely different cart contents never
// share a stale result, while two calls that land within the same short
// window for the same cart state collapse into one network request.
// A 6s client-side timeout keeps a slow/hanging API from stalling the UI
// or the cart lock indefinitely.
window.__shiptectionQuoteCache = window.__shiptectionQuoteCache || { key: null, time: 0, promise: null };
window.__getShiptectionQuoteCached = window.__getShiptectionQuoteCached || function (payload) {
  const key = JSON.stringify(payload);
  const now = Date.now();
  const cache = window.__shiptectionQuoteCache;
  if (cache.key === key && cache.promise && (now - cache.time) < 10000) {
    // stLog('quote served from coalesce cache (identical payload within 10000ms)');
    return cache.promise;
  }
  cache.key = key;
  cache.time = now;
  cache.promise = (async () => {
    stLog('POST /quote payload:', JSON.parse(key));
    const startedAt = performance.now();
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 6000);
    try {
      const response = await fetch('https://api.shiptection.com/quote', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: controller.signal
      });
      if (!response.ok) throw new Error('ShipTection API error: ' + response.status);
      const json = await response.json();
      stLog('/quote response (' + Math.round(performance.now() - startedAt) + 'ms):', json);
      return json;
    } catch (err) {
      cache.key = null; // never serve a failed request from cache
      throw err;
    } finally {
      clearTimeout(timeoutId);
    }
  })();
  return cache.promise;
};


let shippingProtectionLock = false; // module-level, survives element re-creation on re-render
let shippingProtectionDismissed = false; // true after protection removed outside our own add/remove flow
let shippingProtectionWasInCart = false; // last known presence, used to detect external removals
let lastKnownRealItemCount = null; // used to detect a genuinely new real item after dismissal

// Busy state: loading indicator + temporarily blocks the cart drawer while ShipTection is being resolved
let shippingProtectionBusyCount = 0; // counter, so overlapping operations don't release the state early
let shippingProtectionBusyTimer = null;

function applyProtectionBusy(busy) {
  const drawer = document.querySelector('cart-drawer');
  const panel = document.querySelector('cart-drawer .drawer__inner');

  // the widget is re-created on every drawer re-render, so flag every instance currently in the DOM
  document.querySelectorAll('shipping-protection').forEach((el) => {
    el.classList.toggle('shipping-protection-loading', busy);
  });
  if (drawer) drawer.classList.toggle('shipping-protection-busy', busy);
  if (panel) panel.inert = busy; // also blocks keyboard (Tab/Enter) while busy
}

function setProtectionBusy(isBusy) {
  shippingProtectionBusyCount = Math.max(0, shippingProtectionBusyCount + (isBusy ? 1 : -1));
  applyProtectionBusy(shippingProtectionBusyCount > 0);

  clearTimeout(shippingProtectionBusyTimer);
  if (shippingProtectionBusyCount > 0) {
    // safety net: never leave the drawer locked if something hangs
    shippingProtectionBusyTimer = setTimeout(() => {
      stWarn('busy state exceeded 10s, force-releasing');
      shippingProtectionBusyCount = 0;
      applyProtectionBusy(false);
    }, 10000);
  }
}

class ShippingProtection extends HTMLElement {
  connectedCallback() {
    this.checkbox = this.querySelector('#cart-shipping-protection-checkbox');
    if (!this.checkbox) return;

    this.priceEl = this.querySelector('.cart-shipping-protection__price span');

    // Only the set of variant IDs that belong to the ShipTection product —
    // still needed to recognize a ShipTection line in the cart. the API is
    // now the only source for which variant to use.
    this.protectionVariantIds = this.parseProtectionVariantIds();

    // Shop domain for the ShipTection API payload. Prefer the value rendered
    // server-side by Liquid (shop.permanent_domain, always correct and
    // available at render time) over assuming a global exists yet.
    this.shopDomain = this.dataset.shopDomain || (window.Shopify && window.Shopify.shop) || '';

    // Seed from whichever variant is actually in the cart right now
    // (data-variant-id is rendered from in_cart_variant_id, 0 if absent).
    this.variantId = Number(this.checkbox.dataset.variantId) || null;

    this.syncToggleState();

    this.checkbox.addEventListener('change', this.handleToggle.bind(this));

    this.cartUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      this.handleCartUpdate.bind(this)
    );
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
  }

  // read the {ids:[]} payload from Liquid <script type="application/json">
  parseProtectionVariantIds() {
    const script = this.querySelector('#shipping-protection-variants-data');
    if (!script) return [];
    try {
      const parsed = JSON.parse(script.textContent);
      return Array.isArray(parsed.ids) ? parsed.ids.map((id) => Number(id)) : [];
    } catch (e) {
      console.error('Failed to parse shipping protection variant ids:', e);
      return [];
    }
  }

  // every variant id that belongs to the shipping protection product,
  // used to recognize "protection is in the cart" regardless of which specific
  // variant ShipTection currently has us using.
  getProtectionVariantIdSet() {
    return new Set(this.protectionVariantIds);
  }

  // async getCurrentCart() {
  //   const response = await fetch(window.Shopify.routes.root + 'cart.js');
  //   return response.json();
  // }

  async getCurrentCart() {
    return window.__getCartCached();
  }

  getLinerVariantId() {
    try {
      const state = window.__shippingLinerAutoAdd && window.__shippingLinerAutoAdd.getState();
      const resolved = state && Number(state.resolvedVariantId);
      if (Number.isFinite(resolved) && resolved > 0) return resolved;
      const configured = window.shippingLinerAutoAddConfig && Number(window.shippingLinerAutoAddConfig.variantId);
      return Number.isFinite(configured) && configured > 0 ? configured : null;
    } catch (e) {
      return null;
    }
  }

  // single place that goes through cart.items once and returns everything the
  // rest of the component needs, real item count/total (protection + liner
  // excluded), the real merchandise lines shaped for the ShipTection API
  // payload, and whichever protection-product line item is currently in
  // the cart (if any).
  getCartMetrics(cart) {
    const linerVariantId = this.getLinerVariantId();
    const protectionVariantIds = this.getProtectionVariantIdSet();

    let realItemCount = 0;
    let realItemsTotal = 0;
    let protectionItem = null;
    const realItems = [];

    cart.items.forEach((item) => {
      if (protectionVariantIds.has(item.variant_id)) {
        protectionItem = item;
        return;
      }
      if (item.variant_id === linerVariantId) return;
      realItemCount += item.quantity;
      realItemsTotal += item.final_line_price;
      realItems.push({
        product_id: item.product_id,
        price: item.final_price, // unit price in cents; the API multiplies by quantity itself
        quantity: item.quantity
      });
    });

    // stLog('cart metrics', {
    //   currency: cart.currency,
    //   realItemCount,
    //   protectionVariantInCart: protectionItem ? protectionItem.variant_id : null,
    //   linerVariantId,
    //   sentToApi: realItems,
    //   rawCartLines: cart.items.map((i) => ({
    //     variant_id: i.variant_id,
    //     product_id: i.product_id,
    //     qty: i.quantity,
    //     unit_price: i.price,
    //     unit_final_price: i.final_price,
    //     line_price: i.final_line_price
    //   }))
    // });

    return {
      realItemCount,
      realItemsTotal,
      realItems,
      protectionItem,
      protectionInCart: !!protectionItem
    };
  }

  // build the ShipTection API payload from the real merchandise lines and calls POST /quote. 
  // Returns: 
  // { add_shiptection: false } -> synthetically, when there's nothing to quote
  // parsed API response -> on success
  // null -> on any network/HTTP failure, callers must treat null as leave cart alone

  async getShiptectionQuote(realItems, currency) {
    if (!realItems.length) return { add_shiptection: false };

    const payload = {
      shop: this.shopDomain,
      currency: currency,
      items: realItems
    };

    try {
      return await window.__getShiptectionQuoteCached(payload);
    } catch (error) {
      console.error('[ShipTection] quote request FAILED, cart left untouched:', error);
      return null;
    }
  }

  // single entry point used by syncToggleState / handleCartUpdate /
  // handleToggle to get the current recommendation for a given cart snapshot.
  async resolveQuote(cart, metrics) {
    if (metrics.realItemCount === 0) return { add_shiptection: false };
    return this.getShiptectionQuote(metrics.realItems, cart.currency);
  }

  // reflects the API quoted price before the variant has actually been
  // added to the cart. Once protection is in the cart, the re-rendered cart
  // section carries the authoritative Shopify-rendered price for the real
  // in-cart variant, so this is a no-op in that case, we never overwrite a
  // real price with the quote estimate.
  updatePriceDisplay(quote, protectionInCart) {
    if (protectionInCart) return;
    if (!this.priceEl || !quote || !quote.add_shiptection || typeof quote.price !== 'number') return;
    try {
      this.priceEl.textContent = '+' + new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: quote.currency || 'USD'
      }).format(quote.price);
    } catch (e) {
      // formatting failure shouldn't break the widget
    }
  }

  async syncToggleState() {
    setProtectionBusy(true);
    try {
      // stLog('syncToggleState started (page load or drawer re-render)');
      const cart = await this.getCurrentCart();
      const metrics = this.getCartMetrics(cart);
      const { realItemCount, protectionItem, protectionInCart } = metrics;

      // Catches removals that happened via cart-remove-button between element
      // instances, where we never received the cartUpdate event for it.
      if (shippingProtectionWasInCart && !protectionInCart) {
        shippingProtectionDismissed = true;
      }
      // A real item arrived since last check, allow auto-add again
      if (shippingProtectionDismissed && lastKnownRealItemCount !== null && realItemCount > lastKnownRealItemCount) {
        shippingProtectionDismissed = false;
      }

      this.checkbox.checked = protectionInCart;
      if (protectionItem) this.variantId = protectionItem.variant_id;
      shippingProtectionWasInCart = protectionInCart;
      lastKnownRealItemCount = realItemCount;

      // ask shiptection what the correct state is for the current cart.
      // On initial sync we only ever reconcile an existing protection line
      // or remove it if the API says it shouldn't be there, we do not
      // auto-add protection here; that only happens from handleCartUpdate
      const quote = await this.resolveQuote(cart, metrics);
      if (quote === null) { stWarn('syncToggleState: API unavailable, cart left untouched'); return; }

      this.updatePriceDisplay(quote, protectionInCart);

      // stLog('syncToggleState decision', {
      //   apiSaysAdd: quote.add_shiptection,
      //   apiVariantId: quote.id,
      //   apiPrice: quote.price,
      //   inCart: protectionInCart,
      //   currentVariantId: protectionItem ? protectionItem.variant_id : null,
      //   dismissed: shippingProtectionDismissed
      // });

      if (protectionInCart && quote.add_shiptection === false) {
        if (!shippingProtectionDismissed) {
          await this.removeProtection(protectionItem.variant_id);
        }
      } else if (protectionInCart && quote.add_shiptection && quote.id !== protectionItem.variant_id) {
        await this.swapProtectionVariant(protectionItem.variant_id, quote.id);
      }
    } catch (error) {
        console.error('Failed to sync shipping protection state:', error);
    } finally {
      setProtectionBusy(false);
    }
  }

  async handleCartUpdate(event) {
    // stLog('cartUpdate received', { source: event.source, lockHeld: shippingProtectionLock });
    if (
      event.source === 'shipping-protection' ||
      event.source === 'shipping-liner-auto-add' ||
      shippingProtectionLock
    ) {
      // stLog('cartUpdate IGNORED (own event or lock held)');
      return;
    }

    setProtectionBusy(true);

    try {
      const cart = await this.getCurrentCart();
      const metrics = this.getCartMetrics(cart);
      const { realItemCount, protectionItem, protectionInCart } = metrics;

      // Removed via something other than our own methods (e.g. cart-remove-button) — treat as dismissed
      if (shippingProtectionWasInCart && !protectionInCart) {
        shippingProtectionDismissed = true;
      }

      // A genuinely new real item landed since dismissal — allow auto-add again
      if (shippingProtectionDismissed && lastKnownRealItemCount !== null && realItemCount > lastKnownRealItemCount) {
        shippingProtectionDismissed = false;
      }

      // No real merchandise left — protection has nothing to protect.
      // Never call the API with an empty items array (getShiptectionQuote
      // already short-circuits this, but skip the round-trip entirely here).
      if (realItemCount === 0) {
        stLog('no real items in cart, protection should not exist', { protectionInCart });
        if (protectionInCart) {
          await this.removeProtection(protectionItem.variant_id);
          shippingProtectionWasInCart = false;
        } else {
          this.checkbox.checked = false;
        }
        lastKnownRealItemCount = realItemCount;
        return;
      }

      const quote = await this.resolveQuote(cart, metrics);

      if (quote === null) {
        stWarn('handleCartUpdate: API unavailable, cart left untouched');
        // ShipTection API unavailable: do not add, remove, or swap anything.
        // Just resync the checkbox/variantId to whatever is actually in the
        // cart right now so the UI doesn't lie, and try again on the next
        // cart mutation — no retry loop.
        this.checkbox.checked = protectionInCart;
        if (protectionItem) this.variantId = protectionItem.variant_id;
        shippingProtectionWasInCart = protectionInCart;
        lastKnownRealItemCount = realItemCount;
        return;
      }

      this.updatePriceDisplay(quote, protectionInCart);
      
      stLog('handleCartUpdate decision', {
        apiSaysAdd: quote.add_shiptection,
        apiVariantId: quote.id,
        apiPrice: quote.price,
        apiCurrency: quote.currency,
        inCart: protectionInCart,
        currentVariantId: protectionItem ? protectionItem.variant_id : null,
        dismissed: shippingProtectionDismissed,
        realItemCount,
        previousRealItemCount: lastKnownRealItemCount
      });

      if (quote.add_shiptection && !protectionInCart && !shippingProtectionDismissed) {
        await this.addProtection(quote.id);
        shippingProtectionWasInCart = true;
      } else if (!quote.add_shiptection && protectionInCart) {
        await this.removeProtection(protectionItem.variant_id);
        shippingProtectionWasInCart = false;
      } else if (quote.add_shiptection && protectionInCart && quote.id !== protectionItem.variant_id) {
        // ShipTection recommends a different variant than what's currently
        // in the cart (e.g. cart total moved enough to change tiers) — swap it in.
        await this.swapProtectionVariant(protectionItem.variant_id, quote.id);
        shippingProtectionWasInCart = true;
      } else {
        // stLog('no change needed: correct variant already in cart (or dismissed)');
        this.checkbox.checked = protectionInCart;
        if (protectionItem) this.variantId = protectionItem.variant_id;
        shippingProtectionWasInCart = protectionInCart;
      }

      lastKnownRealItemCount = realItemCount;
    } catch (error) {
      console.error('Shipping protection cart sync error:', error);
    } finally {
      setProtectionBusy(false);
    }
  }

  async handleToggle(event) {
    const shouldAdd = event.target.checked;
    stLog('toggle clicked →', shouldAdd ? 'ON' : 'OFF');

    // Disable synchronously (before any await) so a second rapid click/change
    // event can't fire on this checkbox while the first request is in flight.
    // The module-level shippingProtectionLock already prevents a second
    // network call from racing this one, but it doesn't stop the checkbox's
    // own visual state from flipping on every click — this does.
    this.checkbox.disabled = true;
    this.classList.add('shipping-protection-loading');
    setProtectionBusy(true);

    try {
      const cart = await this.getCurrentCart();
      const metrics = this.getCartMetrics(cart);
      const { protectionItem, protectionInCart } = metrics;

      if (shouldAdd === protectionInCart) {
        // stLog('toggle: UI already matches cart, nothing to do');
        return;
      }

      if (shouldAdd) {
        const quote = await this.resolveQuote(cart, metrics);

        if (quote === null) {
          stWarn('toggle ON refused: quote unavailable (API failed)');
          this.checkbox.checked = false;
          return;
        }

        if (!quote.add_shiptection) {
          stLog('toggle ON refused: API says add_shiptection=false', quote);
          // API says this cart doesn't qualify for protection — reflect reality,
          // don't add anything and don't guess a variant.
          this.checkbox.checked = false;
          return;
        }

        // stLog('toggle ON → using API variant', quote.id, 'price', quote.price);
        this.updatePriceDisplay(quote, false);

        if (protectionItem && protectionItem.variant_id !== quote.id) {
          await this.swapProtectionVariant(protectionItem.variant_id, quote.id);
        } else if (!protectionItem) {
          await this.addProtection(quote.id);
        }

        shippingProtectionWasInCart = true;
        shippingProtectionDismissed = false;
      } else {
        // stLog('toggle OFF → removing variant', protectionItem.variant_id, '(will be marked dismissed)');
        await this.removeProtection(protectionItem.variant_id);
        shippingProtectionWasInCart = false;
        // Explicit manual OFF is an intentional dismissal — mark it so the
        // very next cart mutation (e.g. a quantity bump) doesn't silently
        // re-add protection the customer just turned off.
        shippingProtectionDismissed = true;
      }
    } finally {
      this.checkbox.disabled = false;
      this.classList.remove('shipping-protection-loading');
      setProtectionBusy(false);
    }
  }

  // takes the variant id to add and detects a differently-priced protection
  // variant already in the cart so it swaps instead of double-adding.
  async addProtection(variantId) {
    if (shippingProtectionLock) { stWarn('addProtection SKIPPED, lock already held', variantId); return; }
    shippingProtectionLock = true;
    shippingProtectionDismissed = false;

    // stLog('addProtection → variant', variantId);

    try {
      const cart = await this.getCurrentCart();
      const { protectionItem } = this.getCartMetrics(cart);

      if (protectionItem && protectionItem.variant_id === variantId) {
        // stLog('addProtection: that variant is already in the cart, skipping');
        this.checkbox.checked = true; // already there — sync UI, don't re-add
        this.variantId = variantId;
        return;
      }
      if (protectionItem) {
        // stLog('addProtection: different variant in cart, swapping', protectionItem.variant_id, '→', variantId);
        // a different variant is already in the cart — swap it
        await this._performSwap(protectionItem.variant_id, variantId);
        return;
      }

      const data = await window.__withCartLock(async () => {
        const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{ id: String(variantId), quantity: 1 }],
            sections: this.getSectionsToRender(),
            sections_url: window.location.pathname
          })
        });
        if (!response.ok) throw new Error('Shopify Cart API error');
        return response.json();
      });
      this.variantId = variantId;
      stLog('✔ added variant', variantId);
      this.renderSections(data.sections);
    } catch (error) {
      console.error('[ShipTection] ✖ add failed:', error);
      if (this.checkbox) this.checkbox.checked = false;
    } finally {
      shippingProtectionLock = false;
    }
  }

  // takes the variant id to remove instead of reading a fixed this.variantId,
  // needed because the variant in the cart may differ from whatever the
  // component last computed as the "target".
  async removeProtection(variantId) {
    if (shippingProtectionLock) { stWarn('removeProtection SKIPPED, lock already held', variantId); return; }
    shippingProtectionLock = true;

    // stLog('removeProtection → variant', variantId); 

    try {
      const data = await window.__withCartLock(async () => {
        const response = await fetch(window.Shopify.routes.root + 'cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: String(variantId),
            quantity: 0,
            sections: this.getSectionsToRender(),
            sections_url: window.location.pathname
          })
        });

        if (!response.ok) {
          const errBody = await response.json().catch(() => null);
          if (errBody && /no valid id or line/i.test(errBody.message || '')) {
            return null; // already gone — treat as success
          }
          throw new Error('Shopify Cart API error');
        }

        return response.json();
      });

      if (data === null) {
        this.checkbox.checked = false;
        return;
      }
      stLog('✔ removed variant', variantId);
      this.renderSections(data.sections);
    } catch (error) {
      console.error('[ShipTection] ✖ remove failed:', error);
      if (this.checkbox) this.checkbox.checked = true;
    } finally {
      shippingProtectionLock = false;
    }
  }

  // swaps the in-cart protection variant in a single /cart/update.js call
  // (remove old line, add new line, one request) instead of a separate
  // remove-then-add — avoids a flash of "no protection" between the two calls
  // and keeps the operation atomic from the queue's point of view.
  async swapProtectionVariant(oldVariantId, newVariantId) {
    if (shippingProtectionLock) { stWarn('swapProtectionVariant SKIPPED, lock already held'); return; } 
    if (oldVariantId === newVariantId) return;
    shippingProtectionLock = true;

    // stLog('swapProtectionVariant', oldVariantId, '→', newVariantId);

    try {
      await this._performSwap(oldVariantId, newVariantId);
    } catch (error) {
      console.error('Error swapping shipping protection variant:', error);
    } finally {
      shippingProtectionLock = false;
    }
  }

  // Core swap network call, no lock management — caller must already hold the lock.
  async _performSwap(oldVariantId, newVariantId) {
    const updates = {};
    updates[oldVariantId] = 0;
    updates[newVariantId] = 1;

    const data = await window.__withCartLock(async () => {
      const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          updates,
          sections: this.getSectionsToRender(),
          sections_url: window.location.pathname
        })
      });
      if (!response.ok) throw new Error('Shopify Cart API error');
      return response.json();
    });

    this.variantId = newVariantId;
    stLog('✔ swapped variant', oldVariantId, '→', newVariantId);
    this.checkbox.checked = true;
    this.renderSections(data.sections);
  }

  // getSectionsToRender() {
  //   return ['cart-drawer', 'cart-icon-bubble'];
  // }

  getSectionsToRender() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
      return cartDrawer.getSectionsToRender().map((section) => section.id);
    }
    return ['cart-drawer', 'cart-icon-bubble']; // fallback only
  }

  renderSections(sections) {
    const cartDrawer = document.querySelector('cart-drawer');

    if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
      cartDrawer.renderContents({ sections });
    } else {
      this.getSectionsToRender().forEach((sectionId) => {
        const container = document.getElementById(sectionId);
        if (!container || !sections[sectionId]) return;
        const parsed = new DOMParser().parseFromString(sections[sectionId], 'text/html');
        const newContent = parsed.getElementById(sectionId);
        if (newContent) container.innerHTML = newContent.innerHTML;
      });
    }

    publish(PUB_SUB_EVENTS.cartUpdate, { source: 'shipping-protection' });
  }
}

if (!customElements.get('shipping-protection')) {
  customElements.define('shipping-protection', ShippingProtection);
}

window.__shippingProtectionEnsure = function () {
  const el = document.querySelector('shipping-protection');
  if (el && typeof el.handleCartUpdate === 'function') {
    return el.handleCartUpdate({ source: 'external-trigger' });
  }
  return Promise.resolve();
};



// ------------------------UNIt complementary scroll --------------------------
class ComplementarySlider extends HTMLElement {
  constructor() {
    super();
    this.init();
  }

  init() {
    const scrollbarEl = this.querySelector('.swiper-scrollbar');

    this.swiperInstance = new Swiper(this, {
      slidesPerView: 2.1,
      spaceBetween: 8,
      freeMode: {
        enabled: true,
        momentumRatio: 1,
        momentumVelocityRatio: 1,
      },
      grabCursor: true,
      watchSlidesProgress: true,
      preventClicks: true,
      preventClicksPropagation: true,
      slideToClickedSlide: false,
      scrollbar: {
        el: scrollbarEl,
        draggable: true,
        dragSize: 'auto',
      },

      breakpoints: {
        500: {
          slidesPerView: "auto",
          spaceBetween: 16,
        },
        750: {
          slidesPerView: "auto",
          spaceBetween: 16,
        },
      },
    });
  }
}

customElements.define('complementary-slider', ComplementarySlider);